import { Box, Card, Skeleton, Stack } from "@mui/material";
import { useMemo } from "react";

import { ComplianceCard } from "./ComplianceCard";
import { type ComplianceItem, sortByDeadlineAscending } from "./CompliancePane.helpers";

export interface CompliancePaneProps {
  items: ComplianceItem[];
  now: Date;
  activePreviewDocId?: string | null;
  onViewDocument: (documentId: string, fileExtension: string | null) => void;
  onResolveInEManifest: (discrepancyId: string) => void;
  onAddProofOfContact: (processedManifestId: string) => void;
  onCreateExceptionReport: (processedManifestId: string) => void;
  creatingExceptionReport?: boolean;
  loading?: boolean;
  /** Manifest's tenant, forwarded to each card for its doc/submission links. */
  tenantId?: string;
}

/**
 * Renders one compliance kind's cards (EPA-Requested Corrections OR Missing
 * e-Manifests — never mixed; the caller passes items pre-filtered to a single
 * kind since each is now its own top-level section).
 */
export function CompliancePane({ items, now, loading = false, ...cardProps }: CompliancePaneProps) {
  const sortedItems = useMemo(() => sortByDeadlineAscending(items, now), [items, now]);

  if (sortedItems.length > 0) {
    return (
      <Stack spacing={3} sx={{ p: 1.5 }}>
        {sortedItems.map((item) => (
          <ComplianceCard key={item.id} item={item} now={now} {...cardProps} />
        ))}
      </Stack>
    );
  }

  if (loading && items.length === 0) {
    return (
      <Stack spacing={3} sx={{ p: 1.5 }}>
        {Array.from({ length: 2 }, (_, i) => (
          <Card key={i} variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Skeleton variant="rounded" width={88} height={24} />
                <Skeleton variant="rounded" width={72} height={24} />
                <Box sx={{ flexGrow: 1 }} />
                <Skeleton variant="text" width={120} />
              </Stack>
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="40%" />
              <Stack direction="row" spacing={1}>
                <Skeleton variant="rounded" width={120} height={32} />
                <Skeleton variant="rounded" width={140} height={32} />
              </Stack>
            </Stack>
          </Card>
        ))}
      </Stack>
    );
  }

  return null;
}
